import { createRoot } from "react-dom/client";
import { startWebAnalytics } from "./lib/webAnalytics";
import { markBarnPage } from "./lib/metaPixel";
import { activeBarnAppSlug } from "./barnApps/registry";
import App from "./App.tsx";
import { initNativeShell } from "./lib/nativeBootstrap";
import { attachTickerAuth } from "./lib/tickerAuth";

// Geist Variable + Geist Mono Variable — the workhorse and number faces
// for the redesigned UI. Self-hosted via @fontsource-variable so we don't
// depend on Google Fonts CDN at runtime. Imported once here so every
// route gets the fonts without per-page work. See UI_REDESIGN_STANDARDS.md.
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";

import "./index.css";
import "./brand";

/**
 * Auto-reload on stale-bundle errors after a deploy.
 *
 * When Vercel ships a new build, the entry index.js gets a new hash and
 * its lazy-loaded chunks (e.g. HomepageWidgets-XXXXX.js) get new hashes
 * too. Users with the old cached index.js will try to import the old
 * chunk filenames, which 404 on the new deploy, and React renders an
 * error boundary that says "Something went wrong" forever.
 *
 * The fix is to listen for Vite's preloadError event (and the legacy
 * "Failed to fetch dynamically imported module" pattern in onerror /
 * unhandledrejection) and force a hard reload. Once the user reloads
 * they get the fresh index.js that knows about the new chunk hashes.
 *
 * sessionStorage guard prevents reload loops if something else is
 * actually broken. TIME-BASED (2026-08-01): the original once-per-
 * session flag failed on rapid-deploy days — after one auto-recovery,
 * the NEXT deploy's stale chunk in the same session showed the error
 * screen. Now we allow another auto-reload as long as the last one was
 * >20s ago; a genuine boot loop still trips the guard within seconds.
 */
const RELOAD_FLAG = "raisedbeef:chunk-reload";
const RELOAD_COOLDOWN_MS = 20_000;

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    // Safari's wording when the old chunk's address now returns the site's
    // index.html (the catch-all rewrite) instead of JavaScript — what an
    // iPhone with the app open saw after every deploy (owner's screenshot,
    // 2026-10-09: "'text/html' is not a valid JavaScript MIME type.").
    message.includes("is not a valid JavaScript MIME type") ||
    /Loading chunk \d+ failed/.test(message) ||
    /Unable to preload CSS/.test(message)
  );
}

function handleChunkError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false; // reloaded seconds ago — real breakage, stop looping
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage may be blocked; reload anyway
  }
  console.warn("[main] Stale chunk import detected after deploy — reloading.");
  window.location.reload();
  return true;
}

// Vite's official event for preload failures
window.addEventListener("vite:preloadError", (event) => {
  const ev = event as Event & { payload?: unknown };
  if (handleChunkError(ev.payload)) {
    event.preventDefault?.();
  }
});

// Catch-all for unhandled promise rejections from lazy() calls
window.addEventListener("unhandledrejection", (event) => {
  if (handleChunkError(event.reason)) {
    event.preventDefault();
  }
});

// Catch-all for synchronous errors
window.addEventListener("error", (event) => {
  if (handleChunkError(event.error ?? event.message)) {
    event.preventDefault();
  }
});

// Clear the reload flag on a successful page load that lasts >2s
// (so the user can recover from a real persistent failure later)
setTimeout(() => {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignore
  }
}, 2000);

createRoot(document.getElementById("root")!).render(<App />);

// Wire native chrome when running inside the Capacitor shell (no-op on web).
void initNativeShell();

// Let `rbCattleTicker` know who is asking — see src/lib/tickerAuth.ts.
//
// ⚠️ HERE BECAUSE THIS IS WHERE A SESSION CAN EXIST. This repo ships TEN HTML
// documents off NINE entries, and main.tsx serves the only three with a login:
// index.html (the platform, every rbnext route, the barn apps), foote.html and
// walter.html.
// The other seven — rb-home, rb-home-next, rb-home-ticker, solutions,
// barn-leaderboard, weight-map, diesel — are standalone documents with their
// own entries and no login, and most are prerendered under Node. Nothing to
// carry there.
//
// Verified before writing this line: no entry-*.tsx imports App or main.tsx.
// (The three that mention main.tsx say "Deliberately NOT main.tsx".)
//
// Not unsubscribed: this lives as long as the document does.
void attachTickerAuth();

// A barn's app on the web (raisedbeef.com/<barn>, ?barn=) gets no Meta pixel
// and no ad-consent banner — see markBarnPage in src/lib/metaPixel.ts.
if (activeBarnAppSlug()) markBarnPage();

// Pageviews, after the page is up (src/lib/webAnalytics.ts).
startWebAnalytics();
